/* <adg-product-preview> v3 — native Web Component (no framework, no build).
 *   <adg-product-preview product="notecard|postcard|calendar"
 *        assets-base="…" arts='["a.jpg","a.jpg","b.jpg",…]' auto>
 * Sets: duplicate art URLs collapse to unique cards with an ×N badge; swipe
 * left/right (or tap a dot) cycles the set with the spin carrying through.
 * Notecard opens as a clamshell whose OPEN CENTER stays where the closed card
 * was. Postcards may be portrait or landscape (auto-detected). Cards + calendar
 * are fully fluid — they fill whatever width their container gives them.
 * auto: animates, rests 6s after any touch, resumes.
 */
const ART_RECT = { left: 5.15, top: 4.15, width: 89.71, height: 91.69 };
const PLACEHOLDER = "__ADG_PLACEHOLDER__";   // sentinel: an empty picker slot ("Select an image")

class ADGProductPreview extends HTMLElement {
  static observedAttributes = ["arts", "product", "assets-base", "asset-map", "slots", "focus", "label", "hide-dots", "no-interact", "quantity", "interval", "resume-delay", "dormant", "showcase", "placeholder-label"];
  attributeChangedCallback(name) {
    if (!this._mounted) return;
    if (name === "focus") {
      const f = +this.getAttribute("focus");
      if (!Number.isNaN(f)) this.show(f);               // animate there and hold
      return;
    }
    if (name === "label" || name === "quantity") { this._updateHdr?.(); return; }
    if (name === "hide-dots") { this.renderDots?.(); return; }
    if (name === "interval" || name === "resume-delay") return;  // read live by the loop
    clearTimeout(this.timer);
    this.connectedCallback();                            // data change: full re-render
  }

  /* ============ PUBLIC CONTROL API (for the parent page / Liquid app) ============
   *   el.play()            resume the self-running animation
   *   el.pause()           freeze it (until play() is called)
   *   el.show(i, side?)    animate to card i ("front" | "back") and hold a while
   *   attributes: focus="3" (same as show), interval="4000" (auto step ms),
   *   resume-delay="8000" (idle ms before the animation resumes after touches),
   *   no-interact + click -> "adg-open" event (popup handshake), mini, hide-dots…
   */
  play(force) {
    this.lastTouch = 0;
    // `force` = the shopper pressed Play: that is consent to motion, so it
    // runs even with reduced motion on (which otherwise keeps it still).
    if (force) this._motionOk = true;
    if (!this.timer && (this.hasAttribute("auto") || this.hasAttribute("mini"))) this.autoPlay();
  }
  pause() { this.lastTouch = Date.now() + 1e12; }
  show(i, side = "front") {
    if (!this.uniq) return;                    // dormant config instances hold state, not shows
    i = Math.max(0, Math.min(this.uniq.length - 1, i | 0));
    this.lastTouch = Date.now() + (+this.getAttribute("resume-delay") || 2500);
    if (this.product === "postcard") this.go(i, side);
    else if (this.product === "notecard") {
      this.ncJump(i);
      if (side === "back") setTimeout(() => this.ncFlip(1, false), 650);
    } else { this.idx = Math.min(i + 1, this.spreads.length - 1); this.renderCal(); }
  }
  connectedCallback() {
    this._mounted = true;
    if (!this.hasAttribute("role")) {
      this.setAttribute("role", "group");
      this.setAttribute("aria-label", this.getAttribute("label") || "Product preview");
    }
    this._cancelFlights();   // a re-render rebuilds the DOM — abandon any in-flight animation first
    this.base = this.getAttribute("assets-base") ?? "assets/";
    // Shopify's asset_url returns individually-HASHED urls that don't concatenate
    // with a base — so the theme passes an asset-map (filename -> full url) for the
    // template assets (card backs, calendar chrome). Bare names hit the map first.
    try { this.assetMap = JSON.parse(this.getAttribute("asset-map") ?? "null") || null; }
    catch { this.assetMap = null; }
    // BUILD-YOUR-OWN mode: `slots` = the customer's set (url or null per slot).
    //   no picks yet  -> demo mode: rotate the `arts` placeholders, no glow
    //   any pick made -> build mode: ONLY picked cards exist (skipped slots are
    //   invisible), each glows as "yours", dots = picked count
    if (this.hasAttribute("dormant")) {   // config-only instance: holds attributes for the
      this.style.display = "none";        // shell to mirror; renders and runs NOTHING
      return;
    }
    const slotsAttr = this.getAttribute("slots");
    const placeholders = JSON.parse(this.getAttribute("arts") ?? "[]");
    const productAttr = this.getAttribute("product") ?? "postcard";
    // `showcase` = the animated catalog reel (for sliders/showpieces). OFF for BYO,
    // where an empty picker shows a single placeholder ("Select an image") instead.
    const showcase = this.hasAttribute("showcase");
    const picker = slotsAttr != null && !showcase;
    let raw, building = false;
    let justAdded = null;     // POSITION index of the newest pick (never URL-matched: dups are distinct slots)
    this._calSlots = null;
    if (slotsAttr != null) {
      const slots = JSON.parse(slotsAttr);
      const hadPrev = this._prevSlots != null;      // false only on the very first mount
      const prev = this._prevSlots ?? [];
      const picked = slots.filter(Boolean);
      building = picked.length > 0;
      // the slot that most recently GAINED or CHANGED artwork: covers a new pick,
      // a drag-drop reorder (its destination) and a replace. A pure delete or clear
      // (slot -> null) matches nothing, so it re-renders with no showcase jump.
      // First mount honors `focus` instead (open at the shared browse position).
      const changedIdx = hadPrev ? slots.findIndex((v, k) => v && v !== (prev[k] ?? null)) : -1;
      if (productAttr === "calendar") {
        // picker: 12 FIXED months, unpicked = placeholder page (never vanish).
        // showcase: the catalog reel instead.
        this._calSlots = picker ? slots.slice() : null;
        raw = placeholders;
        this._calJustAdded = changedIdx >= 0 ? changedIdx : null;
        justAdded = this._calJustAdded;
      } else if (building) {
        raw = picked;                                               // cards: ONLY the picks — they stack 1,2,3…
        if (changedIdx >= 0)                                        // its index among picked positions
          justAdded = slots.slice(0, changedIdx + 1).filter(Boolean).length - 1;
      } else {
        // picker mode, nothing picked -> ONE placeholder card (not the catalog reel)
        raw = picker ? [PLACEHOLDER] : placeholders;
      }
      this._pickedCount = picked.length;
      this._prevSlots = slots;
    } else { raw = placeholders; this._prevSlots = null; this._pickedCount = 0; }
    this.building = building;
    this._picker = picker;
    // showcase / placeholder empty state carries no meaningful "N OF M" count
    this._placeholderShowcase = slotsAttr != null && !building;
    this.uniq = raw.slice();   // positions, verbatim — duplicates are legitimate slots
    if (this.uniq.length === 0) this.uniq = placeholders.length ? placeholders.slice(0, 1) : [PLACEHOLDER];

    this.counts = Object.fromEntries(this.uniq.map((a) => [a, raw.filter((x) => x === a).length]));
    this.cardIdx = justAdded != null && productAttr !== "calendar" ? Math.min(justAdded, this.uniq.length - 1) : 0;
    this.product = productAttr;
    // a fresh pick shows immediately, does ONE little showcase, then waits
    this.lastTouch = justAdded != null ? Date.now() + (+this.getAttribute("pick-hold") || 3000) : 0;
    if (justAdded != null) {
      clearTimeout(this._showT1); clearTimeout(this._showT2);
      const idx = this.cardIdx;
      if (this.product === "notecard") {
        this._showT1 = setTimeout(() => this.setNc(true, true), 900);
        this._showT2 = setTimeout(() => this.setNc(false, true), 2800);
      } else if (this.product === "postcard") {
        this._showT1 = setTimeout(() => this.go(idx, "back", 1), 900);
        this._showT2 = setTimeout(() => this.go(idx, "front", 1), 2800);
      }
    }
    if (!this.shadowRoot) this.attachShadow({ mode: "open" });  // re-renders reuse the root
    this.shadowRoot.innerHTML = `<style>
      button:focus-visible { outline:2px solid #231f20; outline-offset:2px; box-shadow:0 0 0 4px #fff; }
      :host { display:block; container-type:inline-size; } * { box-sizing:border-box; }
      :host([mini]) .dots, :host([mini]) .rail { display:none !important; }
      :host([mini]) .stage { cursor:pointer; }
      .stage { perspective:1500px; touch-action:pan-y; user-select:none; -webkit-user-select:none;
               -webkit-touch-callout:none; position:relative; }
      /* faces are <img alt="">s: kill native image-drag + let every hit land on the stage,
         or a real mouse drag becomes a ghost-image drag and our gesture dies */
      .stage img { -webkit-user-drag:none; user-drag:none; pointer-events:none; }
      /* calendar gestures are VERTICAL — the stage owns them; pan-y would hand
         touch swipes to the browser as page-scroll and fire pointercancel */
      :host([product="calendar"]) .stage { touch-action:none; }
      img { width:100%; height:100%; object-fit:cover; display:block; pointer-events:none; }
      .pg { width:100%; height:100%; background:#efe9d8; border-radius:2px; overflow:hidden;
            box-shadow:0 8px 26px rgba(0,0,0,.25); position:relative; }
      .pg.paper { background:linear-gradient(173deg, #f4eeda, #e9e2cc); }  /* blank page stock */
      /* Notecard INSIDE is printed on plain white stock — not the cream/beige used
         for the calendar pages (Joel, Jul 16 meeting: "the inside for those cards is
         going to be completely white"). The "blank inside" label stays; its ink is
         --adg-preview-blank-ink, darkened to 4.5:1 by the contrast switch (adg-a11y.css). */
      :host([product="notecard"]) .pg.blank { background:#ffffff; color:var(--adg-preview-blank-ink, #9a9a9a); }
      /* empty picker slot — clean, non-directional "Select an image" face */
      .pg.placeholder { display:flex; flex-direction:column; align-items:center; justify-content:center;
            gap:5%; background:linear-gradient(168deg,#f6f1e6,#ece5d5); color:#a99e83;
            border:1px dashed #cdc3a8; }
      .pg.placeholder .ph-ic { width:26%; max-width:64px; color:#c3b795; }
      .pg.placeholder .ph-ic svg { width:100%; height:100%; display:block; }
      .pg.placeholder .ph-tx { font:600 clamp(8px,4.4cqw,13px) -apple-system,sans-serif;
            letter-spacing:.04em; color:#8f846a; text-align:center; padding:0 8%; }
      /* unpicked calendar month: letterpress invitation */
      .pg.phmo { display:flex; flex-direction:column; align-items:center; justify-content:center;
        gap:8px; border:1px dashed #cfc5a6; }
      .phmo-name { font:700 clamp(13px,5cqw,20px) -apple-system,sans-serif; letter-spacing:.22em;
        color:#b9ac8b; }
      .phmo-hint { font:italic clamp(10px,3.6cqw,14px) Georgia,serif; color:#c4b998; }
      .pg.r180 img { transform:rotate(180deg); }   /* print-rotated back page, shown readable over the top */

      .artpage .art { position:absolute; left:${ART_RECT.left}%; top:${ART_RECT.top}%;
            width:${ART_RECT.width}%; height:${ART_RECT.height}%; }
      .badge { position:absolute; top:-2%; right:-3%; z-index:5; background:#4a6fa5; color:#fff;
            font:700 clamp(10px, 4.5cqw, 15px) -apple-system,sans-serif; padding:.35em .8em; border-radius:99px;
            box-shadow:0 2px 8px rgba(0,0,0,.3); }
      .dots { display:flex; gap:0; justify-content:center; align-items:center;
              padding:14px 0 4px; position:relative; }
      /* every dot owns a CONSTANT slot (the active size) — growing happens INSIDE it,
         so the row never reflows and nothing around it jumps. Slots overlap a little
         (they're hit areas, not visuals) so the small dots keep a tight, deliberate rhythm. */
      .dots button { width:clamp(20px, 7cqw, 28px); height:clamp(20px, 7cqw, 28px); flex:none;
            border:4px solid transparent; border-radius:50%; padding:0; cursor:pointer;
            box-sizing:content-box; background:transparent; position:relative; margin:0 -4px; }
      .dots button.on { z-index:1; }
      .dots button::after { content:""; position:absolute; inset:0; margin:auto; border-radius:50%;
            width:clamp(9px, 3.4cqw, 14px); height:clamp(9px, 3.4cqw, 14px); background:#c9c0a8 center/cover;
            background-image:var(--art, none);
            transition:width .18s cubic-bezier(.3,.1,.3,1), height .18s cubic-bezier(.3,.1,.3,1), box-shadow .18s; }
      .dots button.on::after { width:100%; height:100%;
              outline:1.5px solid #fff; outline-offset:-1px;
              box-shadow:0 1px 5px rgba(35,32,25,.35), 0 0 7px 2px #e8a33d40; }  /* quiet warm halo */
      .dots button, .rail span { transition:transform .18s cubic-bezier(.3,.1,.3,1), color .25s, background .2s;
              transform-origin:center bottom; will-change:transform; }
      /* calendar — halves separated by a real gap; pages rotate about the WIRE axis */
      .cal { position:relative; width:100%; aspect-ratio:720/1872; --bg2:clamp(2px, .9cqw, 5px); }
      .cell { position:absolute; left:0; width:100%; height:calc(50% - var(--bg2)); border-radius:2px;
              box-shadow:0 8px 26px rgba(0,0,0,.25); }   /* the shadow is FURNITURE: it never flies */
      .cell:empty { box-shadow:none; }                    /* bare wall casts nothing */
      .cal .pg { box-shadow:none; }                       /* nothing that flips carries a shadow */
      .cell.t { top:0; } .cell.b { bottom:0; }
      .flipper { position:absolute; left:0; bottom:0; width:100%; height:calc(50% - var(--bg2));
        transform-origin:50% calc(0px - var(--bg2)); transform-style:preserve-3d; will-change:transform; z-index:2; }
      .flipper .face { position:absolute; inset:0; backface-visibility:hidden; }
      .flipper .face.bk { transform:rotateX(180deg); }
      /* wire-o binding: punched slots + double metal loops, repeating crisp SVG.
         Sits ON TOP at the hinge — pages emerge from behind the rings. */
      .wire { position:absolute; left:0; right:0; top:50%; transform:translateY(-50%);
        height:clamp(13px, 5cqw, 20px); z-index:3; pointer-events:none;
        background:url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='36' viewBox='0 0 16 36'><defs><linearGradient id='w' x1='0' y1='0' x2='1' y2='0'><stop offset='0' stop-color='%23a8a8a8'/><stop offset='.35' stop-color='%23ffffff'/><stop offset='.7' stop-color='%236e6e6e'/><stop offset='1' stop-color='%23303030'/></linearGradient></defs><rect x='3.2' y='2' width='9.6' height='5.2' rx='2.6' fill='rgba(14,10,5,.6)'/><rect x='3.2' y='28.8' width='9.6' height='5.2' rx='2.6' fill='rgba(14,10,5,.6)'/><path d='M6.1 4.7 C5.7 13 5.6 22 5.7 31.1' fill='none' stroke='%232a2a2a' stroke-width='2' stroke-linecap='round'/><path d='M9.9 4.7 C10.3 13 10.4 22 10.3 31.1' fill='none' stroke='%232a2a2a' stroke-width='2' stroke-linecap='round'/><path d='M6 4.6 C5.6 13 5.5 22 5.6 31' fill='none' stroke='url(%23w)' stroke-width='1.3' stroke-linecap='round'/><path d='M9.8 4.6 C10.2 13 10.3 22 10.2 31' fill='none' stroke='url(%23w)' stroke-width='1.3' stroke-linecap='round'/></svg>")
          repeat-x center / clamp(9px, 3.4cqw, 14px) 100%,
          linear-gradient(to bottom, transparent 26%, rgba(22,15,8,.38) 45%, rgba(22,15,8,.38) 55%, transparent 74%); }
      /* flat card */
      .card { position:relative; width:100%; aspect-ratio:var(--ar,324/432);
        transform-style:preserve-3d; will-change:transform; }
      .card .face { position:absolute; inset:0; backface-visibility:hidden; }
      .card .face.bk { transform:rotateY(180deg); }
      /* Landscape artwork on a card face.
         The card is a PORTRAIT box (324/432) and reads landscape by turning the
         whole card rotateZ(-90deg) — see posture(). Left alone the art turns
         with it and comes out lying on its side, cropped to portrait first by
         object-fit:cover. So pre-rotate the image the opposite way and swap its
         box dimensions (432u wide x 324u tall, expressed against the portrait
         container): after the card's turn it lands upright and fills the face,
         and cover now crops to landscape, which is the shape it wants. */
      .card .artimg.land { position:absolute; left:50%; top:50%;
        width:133.333%; height:75%; transform:translate(-50%,-50%) rotate(90deg); }
      /* notecard clamshell — .nccenter keeps the OPEN spread centered */
      .nccenter { transition:transform .85s cubic-bezier(.3,.1,.25,1); will-change:transform; }
      /* ONE shadow, on the rotating BODY (which never hides) — it turns and foreshortens
         with the card like the postcard's. Faces carry none: flap+base each had one, so
         every flip changed the shadow count (2 -> 1 -> 2) = the flicker. */
      .ncspin .pg { box-shadow:none; }
      .ncspin { position:relative; width:100%; aspect-ratio:324/432; transform-style:preserve-3d;
        border-radius:2px; box-shadow:0 8px 26px rgba(0,0,0,.25);
        transition:transform 1.05s cubic-bezier(.3,.1,.25,1); will-change:transform; }
      .ncbase { position:absolute; inset:0; transform-style:preserve-3d; }
      .ncbase .face { position:absolute; inset:0; backface-visibility:hidden; }
      .ncbase .face.bk { transform:rotateY(180deg); }
      .flap { position:absolute; inset:0; transform-origin:left center; transform-style:preserve-3d;
        transition:transform .85s cubic-bezier(.3,.1,.25,1); will-change:transform; z-index:2; }
      .flap .face { position:absolute; inset:0; backface-visibility:hidden; }
      .flap .face.bk { transform:rotateY(180deg); }
      /* LANDSCAPE notecard = a TOP-FOLD card. A landscape design can't sit on a
         side-fold card without losing its sides, so the physical product folds
         along the top instead: the body goes landscape and the flap lifts up and
         over the top edge rather than swinging out to the left. The artwork
         itself is never rotated — only the hinge axis changes (rotateY about the
         left edge becomes rotateX about the top edge), which is why the back face
         has to mirror about X here too. applyNc() picks the axis to match. */
      .ncspin.land { aspect-ratio:432/324; }
      .ncspin.land .flap { transform-origin:center top; }
      .ncspin.land .flap .face.bk { transform:rotateX(180deg); }
      .blank { display:flex; align-items:center; justify-content:center; color:#9a8f74;
        font:italic clamp(11px, 4cqw, 16px) -apple-system,sans-serif; }
      /* calendar month rail — letterpress spine; thumbnails only while scrubbing */
      .rail { position:relative; display:flex; justify-content:space-between; align-items:flex-end;
        padding:16px 6px 4px; touch-action:none; cursor:pointer; }
      .rail:focus-visible { outline:2px solid #231f20; outline-offset:2px; box-shadow:0 0 0 4px #fff; border-radius:6px; }
      .rail span { font:600 clamp(9px, 3.6cqw, 14px) -apple-system,sans-serif; letter-spacing:.08em; color:#cabfa1;
        padding:2px 0 9px; min-width:14px; text-align:center; transition:color .25s; position:relative; }
      /* a pip under each month letter carries the state: empty = hollow ring,
         picked = solid warm dot, active = larger orange dot. Letters darken too. */
      .rail span::before { content:""; position:absolute; left:50%; margin-left:-2.5px; bottom:1px;
        width:5px; height:5px; border-radius:50%; background:transparent; box-shadow:inset 0 0 0 1px #d8cfb5;
        transition:background .2s, box-shadow .2s, transform .2s; }
      .rail span.filled { color:#7c6f4f; }
      .rail span.filled::before { background:#c2a25a; box-shadow:none; }   /* has artwork */
      .rail span.on { color:#3a3125; }
      .rail span.on::before { background:#b98a2f; box-shadow:none; transform:scale(1.45); }  /* active month */
      .bubble { position:absolute; bottom:34px; width:clamp(60px, 26cqw, 110px); transform:translateX(-50%);
        background:#fffdf6; border:1px solid #d8ceb2; border-radius:6px; padding:5px 5px 3px;
        box-shadow:0 10px 26px rgba(35,32,25,.3); opacity:0; pointer-events:none;
        transition:opacity .22s, left .18s cubic-bezier(.25,.5,.3,1); z-index:9; }
      .bubble.show { opacity:1; }
      .bubble img { width:100%; aspect-ratio:3/4; object-fit:cover; border-radius:3px; }
      /* The bubble frame was portrait for everything, so object-fit:cover sliced
         the ends off a landscape piece — the art looked the right way up because
         it WAS, it was the frame that was wrong. Landscape gets a landscape
         frame; nothing is rotated. */
      .bubble.land img { aspect-ratio:4/3; }
      .bubble div { text-align:center; font:600 9.5px -apple-system,sans-serif; letter-spacing:.1em;
        color:#5d4632; padding-top:3px; text-transform:uppercase; }
      .hdr { display:flex; justify-content:space-between; align-items:baseline; padding:0 2px 10px; }
      .hdr .lbl { font:700 clamp(13px,5cqw,17px) -apple-system,sans-serif; color:#232019; letter-spacing:.02em; }
      .hdr .cnt { font:600 clamp(10px,3.8cqw,13px) -apple-system,sans-serif; color:#9a8f74; letter-spacing:.08em; }
      .hdr .pill { background:#e8a33d; color:#fff; border-radius:99px; padding:.2em .7em;
                   font-weight:700; letter-spacing:.06em; }
    </style><div class="hdr" style="display:none"><span class="lbl"></span><span class="cnt"></span></div><div class="stage"></div><div class="dots"></div>`;
    this.stage = this.shadowRoot.querySelector(".stage");
    this.toggleAttribute("data-building", this.building);
    this._slotTotal = slotsAttr != null ? JSON.parse(slotsAttr).length : this.uniq.length;
    const focusAttr = this.getAttribute("focus");
    if (justAdded == null && focusAttr != null && !Number.isNaN(+focusAttr))
      this.cardIdx = Math.max(0, Math.min(this.uniq.length - 1, +focusAttr));   // a fresh pick outranks a stale focus
    this.dotsEl = this.shadowRoot.querySelector(".dots");
    const touch = () => { this.lastTouch = Math.max(this.lastTouch || 0, Date.now()); };
    ["pointerdown", "wheel"].forEach((e) => this.addEventListener(e, touch, { capture: true }));
    // hover is NOT an interaction (a parked cursor must not freeze the show) —
    // only real engagement counts: presses, drags, wheel. A HELD press freezes
    // even while motionless, and the idle clock restarts when the press ends.
    this.addEventListener("pointermove", (e) => {
      if (e.pointerType !== "mouse" || e.buttons !== 0) touch();
    }, { capture: true });
    this.addEventListener("pointerdown", () => { this._present = true; }, { capture: true });
    // WCAG 2.2.2 (2026-10-07): the show holds still while the pointer rests
    // on it or keyboard focus is inside it (the month rail, the card dots),
    // and picks up again a moment after either leaves. Was: "hover is NOT an
    // interaction", so the months flipped under a reader's cursor and under
    // the rail they were operating with the keyboard.
    if (!this.__holdWired) { this.__holdWired = true;
      this.addEventListener("pointerenter", (e) => { if (e.pointerType === "mouse") this._hoverHold = true; });
      this.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") { this._hoverHold = false; touch(); } });
      this.addEventListener("focusin", () => { this._focusHold = true; });
      this.addEventListener("focusout", (e) => { if (!this.contains(e.relatedTarget) && !this.shadowRoot.contains(e.relatedTarget)) { this._focusHold = false; touch(); } });
    }
    ["pointerup", "pointercancel"].forEach((e) =>
      this.addEventListener(e, () => { this._present = false; touch(); }, { capture: true }));
    if (!this.__dragKilled) { this.__dragKilled = true;
      this.shadowRoot.addEventListener("dragstart", (e) => e.preventDefault()); }  // Firefox belt-and-braces

    if (this.product === "calendar") this.initCalendar();
    else if (this.product === "notecard") this.initNotecard();
    else this.initPostcard();
    if (this.hasAttribute("no-interact") && !this.hasAttribute("mini")) {
      this.stage.style.pointerEvents = "none";
      this.style.cursor = "pointer";
      if (!this.__openWired) { this.__openWired = true;
        this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("adg-open", {
          bubbles: true, composed: true, detail: { product: this.product, arts: this.uniq, index: this.cardIdx } }))); }
    }
    if (this.hasAttribute("mini")) {
      // banner/thumbnail mode: tiny self-running flip-through; a click hands
      // off to the theme (open the full pop-up version there)
      this.stage.style.pointerEvents = "none";
      this.style.cursor = "pointer";
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("adg-open", {
        bubbles: true, composed: true,
        detail: { product: this.product, arts: this.uniq },
      })));
      this.autoPlay();
    } else if (this.hasAttribute("auto")) this.autoPlay();
  }
  disconnectedCallback() { clearTimeout(this.timer); this._cancelFlights(); }
  _cancelFlights() {
    // A full re-render (data change) rebuilds the stage DOM. Any page animation that
    // was mid-air references the OLD nodes and completes via a pending timer — if that
    // stale completion fires against the fresh DOM it desyncs it (calendar: the top art
    // and bottom grid split onto different months; notecard: the wrong card lands).
    // Abandon every in-flight animation + its timer so only the fresh render stands.
    clearTimeout(this._calT); this.calFlight = null;
    clearTimeout(this._ncT); clearTimeout(this._reopenT); clearTimeout(this._ncOpenT);
    clearTimeout(this._showT1); clearTimeout(this._showT2);
    this.ncFlight = null; this.flight = null;
  }
  _url(name) {   // http passthrough · asset-map (Shopify hashed) · else base+name (local/demo)
    if (name.startsWith("http") || name.startsWith("//") || name.startsWith("/")) return name;
    return (this.assetMap && this.assetMap[name]) || (this.base + name);
  }
  img(src) { return `<img alt="" src="${this._url(src)}">`; }
  artPage(a, m) {
    if (!a) {   // unpicked month: an elegant invitation, not a hole
      const MO = ["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];
      return `<div class="pg paper phmo"><div class="phmo-name">${MO[(m ?? 1) - 1]}</div><div class="phmo-hint">choose your artwork</div></div>`;
    }
    return `<div class="pg artpage">${this.img("cal-pagebg.jpg")}<div class="art">${this.img(a)}</div></div>`;
  }

  /* ---------- dock-style magnification (hover on desktop, scrub on touch) ---------- */
  magnetize(container, itemSel, { baseOn = 1.3, max = 1.9, radius = 46, onScrub = null, onPick = null, onStart = null, onHover = null } = {}) {
    if (container.__magnetized) return;      // ONE instance per container, ever
    container.__magnetized = true;
    const items = () => [...container.querySelectorAll(itemSel)];
    const reset = () => items().forEach((el) => {
      el.style.transform = el.classList.contains("on") ? `scale(${baseOn})` : "scale(1)";
    });
    const apply = (x) => {
      let nearest = null, nd = 1e9;
      items().forEach((el) => {
        const r = el.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(x - c);
        if (d < nd) { nd = d; nearest = el; }
        const grow = Math.max(0, 1 - d / radius);
        // hovered swells; the blue one politely shrinks toward normal meanwhile
        const base = el.classList.contains("on") ? (grow > 0.2 ? 1 : baseOn) : 1;
        el.style.transform = `scale(${Math.max(base, 1 + (max - 1) * grow)})`;
      });
      return nearest;
    };
    let scrubbing = false, moved = false, startX = 0;
    container.addEventListener("pointermove", (e) => {
      this.lastTouch = Date.now();
      if (scrubbing) {                                    // held: ACT (live jumps)
        const n = apply(e.clientX);
        moved = moved || Math.abs(e.clientX - startX) > 6;
        if (n && onScrub) onScrub(n, e);
      } else if (e.pointerType === "mouse" && e.buttons === 0) {
        const n = apply(e.clientX);                       // hover: LOOK only
        if (n && onHover) onHover(n, e);
      }
    });
    container.addEventListener("pointerdown", (e) => {
      scrubbing = true; moved = false; startX = e.clientX;
      try { container.setPointerCapture(e.pointerId); } catch {}
      if (onStart) onStart();
      const n = apply(e.clientX);
      if (n && onScrub) onScrub(n, e);
    });
    const end = (e) => {
      if (!scrubbing) return;
      scrubbing = false;
      const n = apply(e.clientX);
      if (n && onPick) onPick(n);   // tap OR scrub-release: pick it
      reset();
    };
    container.addEventListener("pointerup", end);
    container.addEventListener("pointercancel", end);
    container.addEventListener("pointerleave", () => { if (!scrubbing) reset(); });
    // highlight changes happen OUTSIDE pointer interaction (flights, autoplay) —
    // they call this so the old active item doesn't keep its stale magnified scale
    container.__magIdleReset = () => { if (!scrubbing) reset(); };
    reset();
    return { reset };
  }

  /* ---------- set helpers: dots + badge + horizontal flick ---------- */
  renderDots() {
    this._updateHdr();
    if (this.hasAttribute("hide-dots") || this.product === "calendar"
        || (this.uniq.length < 2 && !this.building)) { this.dotsEl.style.display = "none"; return; }
    let bub = this.dotsEl.querySelector(".bubble");   // KEEP the live bubble node across rebuilds
    this.dotsEl.innerHTML = this.uniq.map((a, i) =>
      `<button class="${i === this.cardIdx ? "on" : ""}" data-i="${i}" aria-label="card ${i + 1}"
        style="${i === this.cardIdx ? `--art:url('${this.artSrc(a)}')` : ""}"></button>`).join("");
    if (!bub) {
      bub = document.createElement("div");
      bub.className = "bubble";
      bub.innerHTML = "<img alt=''><div class='mo'></div>";
    }
    this.dotsEl.appendChild(bub);
    this.dotsEl.querySelectorAll("button").forEach((b) =>
      b.addEventListener("click", () => { this.lastTouch = Date.now(); this.goTo(+b.dataset.i); }));
    this.magnetize(this.dotsEl, "button", {
      baseOn: 1.18,
      onStart: () => { this._scrubIdx = null; this._scrubLive = true; },  // fresh gesture; bubble is PINNED until release
      onHover: (b) => this._dotPeek(b),                   // desktop: thumbnails only
      onScrub: (b) => this._dotScrub(b),                  // held tap/drag: jump
      onPick: () => this._dotEnd(),
    });
    if (!this.dotsEl.__leaveWired) {
      this.dotsEl.__leaveWired = true;
      // mid-drag the cursor wanders on/off the strip constantly — never hide then
      this.dotsEl.addEventListener("pointerleave", () => { if (!this._scrubLive) this._dotEnd(); });
    }
  }
  _posText(calIdx) {   // the one-line "where am I" truth, markup-free (shell chrome consumes it too)
    if (this._placeholderShowcase) return "";   // catalog showcase: no count anywhere (header, dock badge, popup chip)
    const q = +(this.getAttribute("quantity") || 1);
    if (this.product === "calendar") {
      const i = calIdx ?? this.idx;
      const MO = ["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];
      return i === 0 ? "COVER" : i >= 13 ? "THE END" : MO[i - 1];
    }
    if (this.building) return `${this.cardIdx + 1} OF ${this._slotTotal ?? this.uniq.length}`;
    if (this.uniq.length === 1) return q > 1 ? `BULK ×${q}` : "";
    return `${this.cardIdx + 1} OF ${this.uniq.length}` + (q > 1 ? ` ×${q}` : "");
  }
  _emitPos(arg) {
    let index;                                     // the POSITION index show()/focus/mirrors use
    if (this.product === "calendar") {
      const spread = arg != null ? arg : this.idx;
      index = spread <= 0 ? 0 : spread >= 13 ? 11 : spread - 1;
    } else index = arg != null ? arg : this.cardIdx;
    this.dispatchEvent(new CustomEvent("adg-position", {
      detail: { text: this._posText(this.product === "calendar" ? arg : undefined), index } }));
  }
  syncTo(i) {   // FOLLOW a driver instance: same flip, but never touch the idle clock
    if (!this.uniq) return;
    if (this.product === "calendar") { this.calGoTo(Math.max(0, Math.min(11, i | 0)) + 1); return; }
    i = Math.max(0, Math.min(this.uniq.length - 1, i | 0));
    if (i === this.cardIdx) return;
    if (this.product === "postcard") this.go(i, "front");
    else this.ncJump(i);
  }
  calGoTo(target) {   // flip to an arbitrary month spread (mirror follow)
    if (!this.cal) return;
    if (this.calFlight) this.finishCal();               // settle FIRST — idx is stale mid-flight
    if (target === this.idx) return;
    const nxt = this.faces(target);
    this.cal.querySelector(".cell.b").innerHTML = nxt.bottom;
    this.cal.querySelector(".flipper .bk").innerHTML = nxt.top || this.pageBack();
    this.calFlight = { dir: 1, target };
    this.updateRailTo(target);
    this.setFlip(180, true);
    clearTimeout(this._calT);
    this._calT = setTimeout(() => this.finishCal(), 1000);  // safety net; transitionend drives it
  }
  _updateHdr() {
    this._emitPos();                                   // hosts (shell popup/dock) ride along
    const h = this.shadowRoot.querySelector(".hdr");
    const label = this.getAttribute("label");
    if (!h) return;
    if (label == null) { h.style.display = "none"; return; }
    h.style.display = "";
    h.querySelector(".lbl").textContent = label;
    // ONE quiet line carries all quantity truth — dots stay clean:
    //   position ("3 OF 7") · per-design dupes ("· ×3") · bulk ("BULK ×240")
    const q = +(this.getAttribute("quantity") || 1);
    const pill = (t) => `<span class="pill">${t}</span>`;
    let html;
    if (this._placeholderShowcase) html = ""; // catalog showcase: no count until picking starts
    else if (this.building)                 // picking: position OF SLOT TOTAL ("3 OF 8")
      html = `${this.cardIdx + 1} OF ${this._slotTotal ?? this.uniq.length}`;
    else if (this.uniq.length === 1) html = q > 1 ? pill(`BULK ×${q}`) : "";
    else html = `${this.cardIdx + 1} OF ${this.uniq.length}` + (q > 1 ? ` ${pill(`×${q}`)}` : "");
    h.querySelector(".cnt").innerHTML = html;
  }
  _syncDots() {
    // landing housekeeping: update header + highlight WITHOUT rebuilding the strip —
    // a rebuild mid-scrub nukes the bubble and the magnification (visible blink)
    this._updateHdr();
    this._setDotHighlight(this.cardIdx);
  }
  _setDotHighlightAt(i, ms) {
    // the dot switches at the REVEAL (edge-on), not at flip start — simultaneous, not early
    clearTimeout(this._dotHiT);
    this._dotHiT = setTimeout(() => this._setDotHighlight(i), ms);
  }
  _setDotHighlight(i) {
    this.dotsEl.querySelectorAll("button").forEach((x) => {
      const on = +x.dataset.i === i;
      x.classList.toggle("on", on);
      if (on) x.style.setProperty("--art", `url('${this.artSrc(this.uniq[i])}')`);
      else x.style.removeProperty("--art");
    });
    this.dotsEl.__magIdleReset?.();    // same stale-scale reclaim as the month rail
  }
  _bub() { return this.dotsEl.querySelector(".bubble"); }   // always the LIVE bubble
  _bubShow(i, x) {
    const bub = this._bub();
    if (!bub) return;
    clearTimeout(this._bubHideT);                           // a live hover ALWAYS wins over a pending hide
    bub.classList.toggle("land", this.orient?.[i] === "l");  // landscape art, landscape frame
    bub.querySelector("img").src = this.artSrc(this.uniq[i]);
    bub.querySelector(".mo").textContent = `CARD ${i + 1}`;
    if (bub.classList.contains("show")) {
      bub.style.left = `${x}px`;                            // already up: glide between dots
    } else {
      bub.style.transition = "opacity .22s";                // arriving: appear in place, no cross-slide
      bub.style.left = `${x}px`;
      void bub.offsetWidth;
      bub.style.transition = "";
      bub.classList.add("show");
    }
  }
  _dotPeek(b) {                                             // hover: bubble, no movement
    this._bubShow(+b.dataset.i, b.offsetLeft + b.offsetWidth / 2);
  }
  _dotScrub(b) {
    const i = +b.dataset.i;
    this.dotsEl.querySelectorAll("button").forEach((x) => {
      const isB = x === b;
      x.classList.toggle("on", isB);
      if (isB) x.style.setProperty("--art", `url('${this.artSrc(this.uniq[+x.dataset.i])}')`);
      else x.style.removeProperty("--art");
    });
    this._bubShow(i, b.offsetLeft + b.offsetWidth / 2);
    this.lastTouch = Math.max(this.lastTouch, Date.now() + 4000); // linger — but never UNDO an explicit pause()
    if (i !== this._scrubIdx) {
      this._scrubIdx = i;
      if (this.product === "postcard") this.go(i, "front", null, true);  // LIVE scrub-jump
      else this.goTo(i);
    }
  }
  _dotEnd() {
    // grace period: brief cursor slips (magnified dots shift under you) don't blink the bubble
    this._scrubLive = false;
    clearTimeout(this._bubHideT);
    this._bubHideT = setTimeout(() => { const b = this._bub(); if (b) b.classList.remove("show"); }, 350);
    this._scrubIdx = null;
  }
  renderBadge(host) {
    const n = this.counts[this.uniq[this.cardIdx]] ?? 1;
    let b = host.querySelector(".badge");
    if (n > 1) {
      if (!b) { b = document.createElement("div"); b.className = "badge"; host.appendChild(b); }
      b.textContent = `×${n}`;
    } else if (b) b.remove();
  }
  flickTo(dxEnd) {
    // horizontal flick: advance through the set, spin carries through
    if (this.uniq.length < 2) return false;
    if (Math.abs(dxEnd) < 40) return false;
    this.goTo((this.cardIdx + (dxEnd < 0 ? 1 : this.uniq.length - 1)) % this.uniq.length);
    return true;
  }

  /* ================= CALENDAR (unchanged this round) ================= */
  initCalendar() {
    this.spreads = [{ closed: "cal-cover.jpg" }];
    for (let m = 1; m <= 12; m++)
      this.spreads.push({
        art: this._calSlots ? this._calSlots[m - 1] : this.uniq[(m - 1) % this.uniq.length],
        m, grid: `cal-grid-${String(m).padStart(2, "0")}.jpg` });
    this.spreads.push({ end: "cal-back.jpg" });  // the back page READS flipped-over-the-top (print-rotated)
    // idx survives re-renders: only the FIRST mount seeds from `focus` (the shared
    // browse position); later data syncs (delete/edit) stay on the current month,
    // while a fresh pick / reorder / replace showcases its own month just below.
    const firstCalMount = !this._calMounted;
    this._calMounted = true;
    this.idx = Number.isInteger(this.idx) ? Math.min(this.idx, this.spreads.length - 1) : 0;
    if (firstCalMount) {                         // seed month at first mount (state continuity across placements)
      const f0 = this.getAttribute("focus");
      this.idx = (f0 != null && !Number.isNaN(+f0)) ? Math.max(0, Math.min(+f0 + 1, this.spreads.length - 1)) : 0;
    }
    if (this._calJustAdded != null) {            // a month was just picked/moved: open ON it (pick-hold runs)
      this.idx = Math.min(this._calJustAdded + 1, this.spreads.length - 1);
      this._calJustAdded = null;
    }
    this.stage.innerHTML = `<div class="cal">
      <div class="cell t"></div><div class="cell b"></div>
      <div class="flipper"><div class="face fr"></div><div class="face bk"></div></div>
      <div class="wire"></div></div>`;
    this.cal = this.stage.querySelector(".cal");
    // Complete each flip exactly when its fold VISUALLY ends, not on a fixed timer
    // that could fire a hair early and snap the half-folded page flat — which reads
    // as the page (e.g. the cover) "folding down again" over itself. The setTimeout
    // in each flip stays as a fallback; finishCal() no-ops if the flight is already done.
    this.cal.querySelector(".flipper").addEventListener("transitionend", (e) => {
      if (e.target === e.currentTarget && e.propertyName === "transform" && this.calFlight) this.finishCal();
    });
    this.renderCal();
    this.calDrag();
    this.renderDots();
    this.initRail();
  }
  initRail() {
    const MO = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
    const rail = document.createElement("div");
    rail.className = "rail";
    // filled = this month holds artwork (a picked slot, or a showcase art) -> darker
    // letter + solid pip; empty months stay dim with a hollow pip.
    const filled = (i) => this._calSlots ? !!this._calSlots[i] : !!this.spreads[i + 1]?.art;
    rail.innerHTML = MO.map((m, i) => `<span data-m="${i}" class="${filled(i) ? "filled" : ""}">${m[0]}</span>`).join("") +
      `<div class="bubble"><img alt=""><div class="mo"></div></div>`;
    this.dotsEl.replaceWith(rail);
    this.rail = rail;
    this.bubble = rail.querySelector(".bubble");
    // Keyboard (WCAG 2.1.1, 2026-10-07): the rail was pointer-only. It is one
    // slider over the whole calendar — cover, January … December, back —
    // operated with the arrow keys, Home / End and Page Up / Down. The letters
    // are its picture; the slider's value text says the month in words.
    rail.setAttribute("role", "slider");
    rail.setAttribute("tabindex", "0");
    rail.setAttribute("aria-label", "Month");
    rail.setAttribute("aria-valuemin", "0");
    rail.setAttribute("aria-valuemax", String(this.spreads.length - 1));
    rail.querySelectorAll("span").forEach((sp) => sp.setAttribute("aria-hidden", "true"));
    rail.addEventListener("keydown", (e) => {
      const last = this.spreads.length - 1;
      const step = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 3, PageDown: -3 }[e.key];
      let to = null;
      if (step != null) to = this.idx + step;
      else if (e.key === "Home") to = 0;
      else if (e.key === "End") to = last;
      if (to == null) return;
      e.preventDefault();
      to = Math.max(0, Math.min(last, to));
      this.lastTouch = Date.now();
      if (to !== this.idx) { this.idx = to; this.renderCal(); this.updateRail(); }
    });
    const scrub = (e, showBubble) => {
      this.lastTouch = Date.now();
      const r = rail.getBoundingClientRect();
      const frac = Math.min(0.999, Math.max(0, (e.clientX - r.left) / r.width));
      const m = Math.floor(frac * 12);
      if (showBubble) {
        const art = this.spreads[m + 1].art;
        const im = this.bubble.querySelector("img");
        if (art) { im.style.display = ""; im.src = this.artSrc(art); }
        else im.style.display = "none";                     // unpicked month: letter only
        this.bubble.querySelector(".mo").textContent = MO[m];
        this.bubble.style.left = `${(e.clientX - r.left)}px`;
        this.bubble.classList.add("show");
      }
      if (this.idx !== m + 1) { this.idx = m + 1; this.renderCal(); this.updateRail(); }
    };
    let down = false;
    rail.addEventListener("pointerdown", (e) => { down = true; rail.setPointerCapture(e.pointerId); scrub(e, true); });
    rail.addEventListener("pointermove", (e) => { if (down) scrub(e, true); });
    const up = () => { down = false; this.bubble.classList.remove("show"); };
    rail.addEventListener("pointerup", up);
    rail.addEventListener("pointercancel", up);
    this.magnetize(rail, "span", { baseOn: 1.25, max: 1.8, radius: 40,
      onHover: (sp) => {
        const m = +sp.dataset.m;
        const art = this.spreads[m + 1].art;
        const im = this.bubble.querySelector("img");
        if (art) { im.style.display = ""; im.src = this.artSrc(art); }
        else im.style.display = "none";
        this.bubble.querySelector(".mo").textContent = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][m];
        this.bubble.style.left = `${sp.offsetLeft + sp.offsetWidth / 2}px`;
        this.bubble.classList.add("show");
      } });
    if (!rail.__hoverHide) { rail.__hoverHide = true;
      rail.addEventListener("pointerleave", () => this.bubble.classList.remove("show")); }
    this.updateRail();
  }
  updateRail() { this.updateRailTo(this.idx); }
  updateRailTo(i) {                    // the letter moves at LAUNCH, riding the flip
    this._emitPos(i);
    if (!this.rail) return;
    const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const last = this.spreads.length - 1;
    const empty = this._calSlots && i >= 1 && i <= 12 && !this._calSlots[i - 1];
    this.rail.setAttribute("aria-valuenow", String(i));
    this.rail.setAttribute("aria-valuetext",
      i <= 0 ? "Cover" : i >= last ? "Back cover" : MONTHS[i - 1] + (empty ? ", no design yet" : ""));
    this.rail.querySelectorAll("span").forEach((sp) =>
      sp.classList.toggle("on", +sp.dataset.m === i - 1));
    this.rail.__magIdleReset?.();      // reclaim the old letter's magnified scale
  }
  faces(i) {
    const s = this.spreads[Math.max(0, Math.min(i, this.spreads.length - 1))];
    // closed cover: nothing hangs above — the calendar is unopened
    if (s.closed) return { top: "", bottom: `<div class="pg">${this.img(s.closed)}</div>` };
    // the END: everything has flipped over the top — back page reads ABOVE the wire
    // (print rotation and the physical flip cancel out); below: bare wall, nothing
    if (s.end) return { top: `<div class="pg r180">${this.img(s.end)}</div>`, bottom: "" };
    return { top: this.artPage(s.art, s.m), bottom: `<div class="pg">${this.img(s.grid)}</div>` };
  }
  renderCal() {
    // a full static compose supersedes any flip in progress (e.g. rail scrub mid-flight) —
    // drop the stale flight so its completion timer can't re-shuffle these fresh faces
    if (this.calFlight) { clearTimeout(this._calT); this.calFlight = null; }
    const cur = this.faces(this.idx), nxt = this.faces(this.idx + 1);
    this.cal.querySelector(".cell.t").innerHTML = cur.top;
    this.cal.querySelector(".cell.b").innerHTML = cur.bottom;
    this.cal.querySelector(".flipper .fr").innerHTML = cur.bottom;
    this.cal.querySelector(".flipper .bk").innerHTML = nxt.top;
    this.setFlip(0);
    this.cal.querySelector(".flipper").style.visibility = "hidden";   // hidden at rest; shown only while turning
    this.updateRail();
  }
  setFlip(deg, animate = false) {
    const f = this.cal.querySelector(".flipper");
    // the flipper is an ANIMATION element only — reveal it while a page is turning.
    // At rest it is hidden (below), so its redundant front + back faces never read as
    // a phantom "extra page" over the static cells (backface-visibility can't be relied on).
    if (deg !== 0 || animate) f.style.visibility = "visible";
    f.style.transition = animate ? "transform .6s cubic-bezier(.3,.1,.3,1)" : "none";
    f.style.transform = `rotateX(${-deg}deg)`;
  }
  pageBack() {   // fallback for a page with no front (only the cover) — render NOTHING
    return "";   // (was a blank tan sheet, which read as a phantom page above the cover)
  }
  calNext(fast = false) {
    if (this.calFlight) this.finishCal();               // snap-complete, then proceed
    if (this.idx >= this.spreads.length - 1) {          // the END: nothing left to lift —
      const cover = this.faces(0);                      // the stack ROTATES BACK DOWN to closed
      this.cal.querySelector(".flipper .fr").innerHTML = cover.bottom;          // cover rides the front
      this.cal.querySelector(".flipper .bk").innerHTML = this.faces(this.idx).top; // back page rides down
      this.cal.querySelector(".cell.t").innerHTML = "";  // bare wall revealed as it descends
      this.setFlip(180);                                 // raise instantly (pixel-identical, covered)
      this.calFlight = { dir: -1, target: 0 };
      this.updateRailTo(0);
      requestAnimationFrame(() => requestAnimationFrame(() => this.setFlip(0, true)));
      clearTimeout(this._calT);
      this._calT = setTimeout(() => this.finishCal(), 1000);  // safety net only; transitionend drives it
      return;
    }
    const target = this.idx + 1;
    const nxt = this.faces(target);
    this.cal.querySelector(".cell.b").innerHTML = nxt.bottom;
    this.cal.querySelector(".flipper .bk").innerHTML = nxt.top || this.pageBack();
    this.calFlight = { dir: 1, target };
    this.updateRailTo(target);
    this.setFlip(180, true);
    clearTimeout(this._calT);
    this._calT = setTimeout(() => this.finishCal(), fast ? 700 : 1000);  // safety net; transitionend drives it
  }
  calPrev() {   // step BACK one spread — the leaf descends (mirror of calNext, for scroll-up)
    if (!this.cal) return;
    if (this.calFlight) this.finishCal();               // settle FIRST — idx is stale while a flip is mid-air
    if (this.idx <= 0) return;                          // already at the cover: nothing before it
    // (guard-before-settle here caused the "double cover": a 2nd reverse step arriving
    // mid-flight passed the stale-idx guard, then built a phantom cover-close from idx 0)
    const prev = this.faces(this.idx - 1), cur = this.faces(this.idx);
    this.cal.querySelector(".flipper .fr").innerHTML = prev.bottom;
    this.cal.querySelector(".flipper .bk").innerHTML = cur.top || this.pageBack();
    this.cal.querySelector(".cell.t").innerHTML = prev.top;   // revealed as the leaf comes down
    this.setFlip(180);                                        // raised instantly (covered)…
    this.calFlight = { dir: -1, target: this.idx - 1 };
    this.updateRailTo(this.idx - 1);
    requestAnimationFrame(() => requestAnimationFrame(() => this.setFlip(0, true)));   // …then descend
    clearTimeout(this._calT);
    this._calT = setTimeout(() => this.finishCal(), 1000);  // safety net; transitionend drives it
  }
  calGoPicked() {   // build-mode idle: hop ONLY through picked months, skip the invitations
    if (!this._calSlots) return this.calNext();
    if (this.calFlight) this.finishCal();                 // settle any prior flight FIRST, so idx is current
    const picked = [];
    for (let m = 1; m <= 12; m++) if (this._calSlots[m - 1]) picked.push(m);   // spread idx == month #
    if (picked.length <= 1) return;                       // one pick: hold on it, no dead rotation
    let target = picked.find((p) => p > this.idx);
    if (target == null) target = picked[0];               // wrap to the first picked month
    const nxt = this.faces(target);                       // single page-lift reveals the target month
    this.cal.querySelector(".cell.b").innerHTML = nxt.bottom;
    this.cal.querySelector(".flipper .bk").innerHTML = nxt.top || this.pageBack();
    this.calFlight = { dir: 1, target };
    this.updateRailTo(target);
    this.setFlip(180, true);
    clearTimeout(this._calT);
    this._calT = setTimeout(() => this.finishCal(), 1000);  // safety net; transitionend drives it
  }
  finishCal() {
    if (!this.calFlight) return;
    const { dir, target } = this.calFlight;
    this.calFlight = null;
    clearTimeout(this._calT);
    const t = this.cal.querySelector(".cell.t"), b = this.cal.querySelector(".cell.b");
    const fr = this.cal.querySelector(".flipper .fr"), bk = this.cal.querySelector(".flipper .bk");
    this.idx = Math.max(0, Math.min(target, this.spreads.length - 1));   // never land outside the book
    const cur = this.faces(this.idx);
    if (dir === 1) {
      t.replaceChildren(...bk.childNodes);              // landed page -> top cell: same NODES, it PERSISTS
      fr.replaceChildren(...b.childNodes);              // revealed grid -> flipper front: same NODES
      b.innerHTML = cur.bottom;                         // hidden understudy refilled under cover
      bk.innerHTML = this.faces(this.idx + 1).top;
    } else {
      b.innerHTML = cur.bottom;                         // back-landing: fr already shows this grid,
      bk.innerHTML = this.faces(this.idx + 1).top;      // top cell already shows this art (set at drag start)
    }
    // guard: an interrupted flip can leave a stale page above the wire. The cover is
    // the ONLY spread with no top page, so its top cell must be empty — otherwise a
    // phantom (tan) page lingers there and appears to "double-close" on the next flip.
    if (!cur.top) t.innerHTML = "";
    this.setFlip(0);
    this.cal.querySelector(".flipper").style.visibility = "hidden";   // at rest the static cells stand alone
    this.updateRail();
  }
  calDrag() {
    let sy = null, mode = null, t0 = 0;
    let end;   // hoisted: the stray-release guard must RELEASE, not cancel — real mice
               // often send a buttons:0 move BEFORE pointerup, which was eating the gesture
    this.stage.addEventListener("pointerdown", (e) => { sy = e.clientY; mode = null; t0 = performance.now(); this.dragging = false; try { this.stage.setPointerCapture(e.pointerId); } catch {} });
    this.stage.addEventListener("pointermove", (e) => {
      if (sy == null) return;
      if (e.pointerType === "mouse" && e.buttons === 0) { end(); return; }
      const dy = e.clientY - sy;
      if (Math.abs(dy) > 8) this.dragging = true;
      if (!this.dragging) return;
      if (!mode) {
        mode = dy < 0 ? "fwd" : "back";
        if (mode === "fwd") {
          if (this.calFlight) this.finishCal();
          // at the END the board can't lift — swipe up is inert there (tap rotates the stack down)
          if (this.idx >= this.spreads.length - 1) { mode = null; this.dragging = false; return; }
          // same rule as calNext: the reveal must be REAL from the first degree of lift
          const nxt = this.faces(this.idx + 1);
          this.cal.querySelector(".cell.b").innerHTML = nxt.bottom;
          this.cal.querySelector(".flipper .bk").innerHTML = nxt.top || this.pageBack();
        } else {
          if (this.calFlight) this.finishCal();          // settle FIRST — idx is stale mid-flight
          if (this.idx <= 0) { mode = null; this.dragging = false; return; }
          const prev = this.faces(this.idx - 1), cur = this.faces(this.idx);
          this.cal.querySelector(".flipper .fr").innerHTML = prev.bottom;
          this.cal.querySelector(".flipper .bk").innerHTML = cur.top || this.pageBack();
          this.cal.querySelector(".cell.t").innerHTML = prev.top;   // revealed as the leaf descends
        }
      }
      this.deg = mode === "fwd" ? Math.min(-dy * 0.85, 180) : Math.max(180 - dy * 0.85, 0);
      this.setFlip(Math.max(0, this.deg));
    });
    end = () => {
      if (sy == null) return;
      sy = null;
      if (!this.dragging) { this.calNext(); return; }  // TAP advances (capture retargets clicks, so
      if (!mode) return;                               // the tap lives HERE, not in a click listener)
      const quick = performance.now() - t0 < 300;      // a FLICK commits on intent, not on distance
      if (mode === "fwd") {
        if (this.deg > 80 || (quick && this.deg > 22)) {   // commit: finish the lift as a real flight
          const target = this.idx >= this.spreads.length - 1 ? 0 : this.idx + 1;
          this.calFlight = { dir: 1, target };
          this.updateRailTo(target);
          this.setFlip(180, true);
          clearTimeout(this._calT);
          this._calT = setTimeout(() => this.finishCal(), 420);
        } else {                                   // cancel: settle first, restore canon only when covered
          this.setFlip(0, true);
          clearTimeout(this._calT);
          this._calT = setTimeout(() => this.renderCal(), 420);
        }
      } else if (this.deg < 100 || (quick && this.deg < 158)) {  // commit BACK: leaf completes its descent
        this.calFlight = { dir: -1, target: this.idx - 1 };
        this.updateRailTo(this.idx - 1);
        this.setFlip(0, true);
        clearTimeout(this._calT);
        this._calT = setTimeout(() => this.finishCal(), 420);
      } else {                                     // cancel back: leaf returns up; canon after it covers
        this.cal.querySelector(".cell.t").innerHTML = this.faces(this.idx).top;
        this.setFlip(180, true);
        clearTimeout(this._calT);
        this._calT = setTimeout(() => this.renderCal(), 420);
      }
      setTimeout(() => { this.dragging = false; }, 60);
    };
    this.stage.addEventListener("pointerup", end);
    this.stage.addEventListener("pointercancel", end);
    // scroll-wheel flips months (desktop): down = forward, up = back. One notch =
    // one flip; a trackpad's many small deltas accumulate to a step. Acts only on a
    // deliberate vertical scroll, and only where the stage is live (popup / inline).
    let wheelAcc = 0, wheelAt = 0;
    this.stage.addEventListener("wheel", (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;   // sideways scroll: leave the page alone
      e.preventDefault();
      this.lastTouch = Date.now();
      const now = performance.now();
      if (now - wheelAt > 260) wheelAcc = 0;                  // a pause starts a fresh gesture
      wheelAt = now;
      wheelAcc += e.deltaY;
      if (wheelAcc >= 30) { wheelAcc = 0; this.calNext(); }
      else if (wheelAcc <= -30) { wheelAcc = 0; this.calPrev(); }
    }, { passive: false });
  }

  /* ================= POSTCARD — the flip ENGINE =================
   * One primitive: go(targetIdx, targetSide). From ANY state (any card, front
   * or back, even mid-animation) it: snap-completes whatever is in flight,
   * loads the destination content onto the HIDDEN face, flips 180 in the
   * natural direction with the destination's posture (sideways for backs and
   * landscape fronts), then normalizes geometry invisibly. Random access. */
  initPostcard() {
    this.stage.innerHTML = `<div class="card">
      <div class="face fr"><div class="pg"></div></div>
      <div class="face bk"><div class="pg"></div></div></div>`;
    this.card = this.stage.querySelector(".card");
    this.rot = 0;                 // cumulative Y rotation
    this.showing = "front";
    this.flight = null;           // in-flight transition descriptor
    this.orient = this.uniq.map(() => "p");
    this.uniq.forEach((a, i) => {
      if (a === PLACEHOLDER) return;              // sentinel, not an image — don't probe
      const probe = new Image();
      probe.onload = () => {
        if (probe.naturalWidth <= probe.naturalHeight) return;
        this.orient[i] = "l";
        // The face HTML carries the .land class, so the CONTENT has to be
        // rebuilt too — bumping the card transform alone leaves the art on
        // its side until the next flip happens to re-render that face.
        if (i === this.cardIdx && !this.flight) { this.canonFaces(); this.applyCard(false); }
      };
      probe.src = this.artSrc(a);
    });
    this.canonFaces();
    this.applyCard(false);
    this.card.addEventListener("transitionend", (e) => { if (e.propertyName === "transform") this.finishFlight(); });

    // drag = a TRIGGER, not a hand-spin: a little tactile give, and the moment the
    // drag crosses the threshold it COMMITS to the normal flip in that direction
    let sx = null;
    const COMMIT = 36;
    this.stage.addEventListener("pointerdown", (e) => { sx = e.clientX; this.dragging = false; this.stage.setPointerCapture(e.pointerId); });
    this.stage.addEventListener("pointermove", (e) => {
      if (sx == null) return;
      if (e.pointerType === "mouse" && e.buttons === 0) { sx = null; this.dragging = false; this.applyCard(true); return; }
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 8) this.dragging = true;
      if (!this.dragging) return;
      if (Math.abs(dx) >= COMMIT) {                       // intent is clear: fire the real flip
        sx = null; this.dragging = false;                 // gesture consumed — one advance per swipe
        // the flip CONTINUES the lean: push right → card keeps turning right → next card
        const dir = dx > 0 ? 1 : -1;
        this.go((this.cardIdx + (dx > 0 ? 1 : this.uniq.length - 1)) % this.uniq.length, "front", dir);
      } else if (!this.flight) {                          // below threshold: capped give, springs back
        this.card.style.transition = "none";
        this.card.style.transform = `rotateY(${this.rot + Math.max(-10, Math.min(10, dx * 0.3))}deg)${this.posture(this.showing, this.cardIdx)}`;
      }
    });
    const up = () => {
      if (sx == null) return; sx = null;
      if (this.dragging) { this.dragging = false; this.applyCard(true); }   // released early: spring home
      else this.go((this.cardIdx + 1) % this.uniq.length, "front", 1);      // tap: advance, fronts only
    };
    this.stage.addEventListener("pointerup", up);
    this.stage.addEventListener("pointercancel", up);
    this.stage.addEventListener("dblclick", () =>         // the back, on request only
      this.go(this.cardIdx, this.showing === "front" ? "back" : "front"));
    this.renderDots();
  }
  artSrc(a) { return this._url(a); }
  content(side, idx) {
    if (side === "front" && this.uniq[idx] === PLACEHOLDER) return this.placeholderFront();
    // `land` counter-rotates the art inside the card, which posture() is about
    // to turn -90deg. Without it a landscape piece prints on its side.
    const land = this.orient?.[idx] === "l" ? " land" : "";
    return side === "back" ? this.img("pc-back.jpg")
      : `<img alt="" class="artimg${land}" src="${this.artSrc(this.uniq[idx])}">`;
  }
  placeholderFront() {   // clean, non-directional empty-slot face ("Select an image")
    const label = this.getAttribute("placeholder-label") || "Select an image";
    return `<div class="pg placeholder"><span class="ph-ic">` +
      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">` +
      `<rect x="3" y="4.5" width="18" height="15" rx="2.2"/><circle cx="8.5" cy="10" r="1.7"/><path d="M4 16.5l4.5-4.2 3.6 3.4 3.2-3 4.7 4.3"/></svg>` +
      `</span><span class="ph-tx">${label}</span></div>`;
  }
  posture(side, idx) {
    return (side === "back" || this.orient?.[idx] === "l") ? " rotateZ(-90deg)" : "";
  }
  canonFaces() {
    // canonical rest state: front face = current card, back face = the back
    this.card.querySelector(".face.fr .pg").innerHTML = this.content("front", this.cardIdx);
    this.card.querySelector(".face.bk .pg").innerHTML = this.content("back", this.cardIdx);
  }
  applyCard(animate) {
    this.card.style.transition = animate ? "transform .75s cubic-bezier(.3,.1,.25,1)" : "none";
    this.card.style.transform = `rotateY(${this.rot}deg)${this.posture(this.showing, this.cardIdx)}`;
  }
  go(targetIdx, targetSide, dirHint = null, fast = false) {
    if (this.product !== "postcard") return;
    if (this.flight) this.finishFlight();                 // snap-complete, then proceed
    if (targetIdx === this.cardIdx && targetSide === this.showing) return;
    const n = this.uniq.length;
    const dir = dirHint ?? (targetIdx === this.cardIdx ? 1
      : (((targetIdx - this.cardIdx + n) % n) <= n / 2 ? 1 : -1));   // flip toward the target
    // parity says which physical face is hidden right now
    const frontVisible = ((Math.round(this.rot / 180) % 2) + 2) % 2 === 0;
    const hidden = this.card.querySelector(frontVisible ? ".face.bk .pg" : ".face.fr .pg");
    hidden.innerHTML = this.content(targetSide, targetIdx);
    this.flight = { targetIdx, targetSide };
    if (targetIdx !== this.cardIdx) this._emitPos(targetIdx);   // mirrors start their flip at LAUNCH, together
    this._setDotHighlightAt(targetIdx, fast ? 70 : 170);    // leads the reveal by the dot's own .18s grow — they finish together
    this.rot += 180 * dir;
    this.card.style.transition = fast ? "transform .34s cubic-bezier(.35,.15,.3,1)"
                                      : "transform .75s cubic-bezier(.3,.1,.25,1)";
    this.card.style.transform = `rotateY(${this.rot}deg)${this.posture(targetSide, targetIdx)}`;
  }
  finishFlight() {
    if (!this.flight) return;
    const { targetIdx, targetSide } = this.flight;
    this.flight = null;
    this.cardIdx = targetIdx;
    this.showing = targetSide;
    // normalize by MOVING the flown-in nodes to the canonical face — rewriting the
    // visible face re-creates the <img alt=""> and blinks the page + its shadow for a frame
    const wasOdd = ((Math.round(this.rot / 180) % 2) + 2) % 2 === 1;
    const fr = this.card.querySelector(".face.fr .pg");
    const bk = this.card.querySelector(".face.bk .pg");
    const visible = wasOdd ? bk : fr;
    const canon = targetSide === "front" ? fr : bk;
    if (visible !== canon) canon.replaceChildren(...visible.childNodes);
    (canon === fr ? bk : fr).innerHTML = this.content(targetSide === "front" ? "back" : "front", targetIdx);
    this.rot = targetSide === "front" ? 0 : 180;
    this.applyCard(false);
    this._syncDots();
  }
  setArtImg() { this.canonFaces(); this.renderDots(); }

  goTo(i) {                                  // pure router — the ENGINE owns cardIdx.
    // (Pre-setting cardIdx here made ncJump see "already there" and no-op: the
    // long-standing "notecard dots don't work" bug.)
    if (this.product === "postcard") this.go(i, "front");
    else this.ncJump(i);
  }

  /* ================= NOTECARD (clamshell, centered) ================= */
  initNotecard() {
    this.stage.innerHTML = `<div class="nccenter"><div class="ncspin">
      <div class="ncbase">
        <div class="face fr"><div class="pg blank">blank inside</div></div>
        <div class="face bk"><div class="pg">${this.img("nc-back-panel.jpg")}</div></div>
      </div>
      <div class="flap">
        <div class="face fr"><div class="pg"><img alt="" class="artimg"></div></div>
        <div class="face bk"><div class="pg blank">blank inside</div></div>
      </div></div></div>`;
    this.center = this.stage.querySelector(".nccenter");
    this.spin = this.stage.querySelector(".ncspin");
    this.flap = this.stage.querySelector(".flap");
    this.open = false;
    this.half = 0;          // count of 180-degree flips; even = front, odd = back
    this.ncPhase = 0;       // auto-show: 0 open-peek, 1 close, 2 flip-to-back, 3 flip-to-next
    // Which picks are landscape — drives the top-fold geometry. Probed the same
    // way the postcard does it, because the picker's data carries no orientation.
    this.orient = this.uniq.map(() => "p");
    this.uniq.forEach((a, i) => {
      if (a === PLACEHOLDER) return;
      const probe = new Image();
      probe.onload = () => {
        if (probe.naturalWidth <= probe.naturalHeight) return;
        this.orient[i] = "l";
        if (i === this.cardIdx) { this.setNcArt(); this.applyNc(false); }
      };
      probe.src = this.artSrc(a);
    });
    this.setNcArt();
    this.applyNc(false);
    let sx = null;
    this.stage.addEventListener("pointerdown", (e) => {
      if (this.ncFlight) this.finishNc();          // settle anything airborne
      clearTimeout(this._reopenT);
      sx = e.clientX; this.dragging = false;
      try { this.stage.setPointerCapture(e.pointerId); } catch {}
    });
    this.stage.addEventListener("pointermove", (e) => {
      if (sx == null) return;
      if (e.pointerType === "mouse" && e.buttons === 0) {           // stray release
        sx = null; if (this.dragging) this.applyNc(true); this.dragging = false; return;
      }
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 8) this.dragging = true;
      if (!this.dragging) return;
      if (Math.abs(dx) >= 36) {                                     // commit: real fold-flip
        sx = null; this.dragging = false;                           // one advance per swipe
        // continue the lean (spin space is half*-180, so rightward = dir -1): right → next
        this.ncJump((this.cardIdx + (dx > 0 ? 1 : this.uniq.length - 1)) % this.uniq.length, dx > 0 ? -1 : 1);
      } else if (!this.ncFlight && !this.open) {                    // tactile give only, capped
        this.spin.style.transition = "none";
        this.spin.style.transform = `rotateY(${this.half * -180 + Math.max(-10, Math.min(10, dx * 0.3))}deg)`;
      }
    });
    this.stage.addEventListener("pointerup", () => {
      if (sx == null) return; sx = null;
      if (this.dragging) { this.dragging = false; this.applyNc(true); }  // released early: spring home
      else this.ncJump((this.cardIdx + 1) % this.uniq.length, 1);        // tap = advance
    });
    this.stage.addEventListener("dblclick", () => this.setNc(!this.open, true)); // peek
    this.renderDots();
  }
  setNcArt() {
    // Landscape picks fold along the top, so the whole body changes shape.
    if (this.spin) this.spin.classList.toggle("land", this.orient?.[this.cardIdx] === "l");
    const flapPg = this.stage.querySelector(".flap .face.fr .pg");
    if (this.uniq[this.cardIdx] === PLACEHOLDER) {
      if (!flapPg.querySelector(".placeholder")) flapPg.innerHTML = this.placeholderFront();
    } else {
      let im = flapPg.querySelector(".artimg");
      if (!im) { flapPg.innerHTML = `<img alt="" class="artimg">`; im = flapPg.querySelector(".artimg"); }
      const want = this.artSrc(this.uniq[this.cardIdx]);
      if (im.getAttribute("src") !== want) im.src = want;        // same art = no touch, no re-decode
    }
    this._syncDots();    // light sync — rebuilding the strip mid-gesture blinks the bubble
  }
  setNc(open, animate = false) {
    if (open && ((this.half % 2) + 2) % 2 !== 0) {
      // facing backward: flip home first, THEN open — never open in reverse
      this.ncFlip(1, false);
      clearTimeout(this._reopenT);
      this._reopenT = setTimeout(() => this.setNc(true, animate), 1150);
      return;
    }
    this.open = open;
    this.applyNc(animate);
  }
  ncJump(target, dir = null) {
    // ANY position -> front of ANY card: open folds shut first; from the back
    // the incoming art rides the hidden flap-front; from the front it rides
    // the hidden base-back. Interrupt-safe throughout.
    if (this.ncFlight) this.finishNc();
    const onFront = ((this.half % 2) + 2) % 2 === 0;
    if (target === this.cardIdx && !this.open && onFront) return;
    dir = dir ?? (target >= this.cardIdx ? 1 : -1);
    if (this.open) {
      this.setNc(false, true);                        // clean fold, then travel
      clearTimeout(this._ncOpenT);
      this._ncOpenT = setTimeout(() => this.ncJump(target, dir), 900);
      return;
    }
    if (target !== this.cardIdx) this._emitPos(target);   // mirrors start together at LAUNCH
    this._setDotHighlightAt(target, 90);     // leads the .38s fold's edge-on so dot and reveal finish together
    let saved = null;
    if (onFront) {                                     // front -> next front via base-back
      const basePg = this.stage.querySelector(".ncbase .face.bk .pg");
      saved = basePg.innerHTML;
      basePg.innerHTML = `<img alt="" src="${this.artSrc(this.uniq[target])}">`;
    } else {                                           // back -> front via hidden flap
      const flapPg = this.stage.querySelector(".flap .face.fr .pg");
      flapPg.innerHTML = `<img alt="" class="artimg" src="${this.artSrc(this.uniq[target])}">`;
    }
    this.ncFlight = { target, saved };
    this.half += dir;
    this.applyNc(true, true);
    clearTimeout(this._ncT);
    this._ncT = setTimeout(() => this.finishNc(), 440);
  }
  finishNc() {
    const f = this.ncFlight;
    if (!f) return;
    this.ncFlight = null;
    clearTimeout(this._ncT);
    if (f.saved != null) {
      // the incoming art flew in on the base back: MOVE that exact <img alt=""> node to the
      // flap front (no reload, no shadow blink), then restore the real About panel
      const basePg = this.stage.querySelector(".ncbase .face.bk .pg");
      const flapPg = this.stage.querySelector(".flap .face.fr .pg");
      const img = basePg.querySelector("img");
      if (img) { img.className = "artimg"; flapPg.replaceChildren(img); }
      basePg.innerHTML = f.saved;
    }
    this.cardIdx = f.target;
    this.half = Math.round(this.half / 2) * 2;         // canonical: front-facing
    this.setNcArt();
    [this.spin, this.flap, this.center].forEach((el) => { el.style.transition = "none"; });
    this.applyNc(false);
  }
  ncFlip(dir = 1, advance = true) {          // one 180 — like turning a real card over
    if (this.open) { this.setNc(false, true); setTimeout(() => this.ncFlip(dir, advance), 950); return; }
    const landsOnFront = ((this.half + dir) % 2 + 2) % 2 === 0;
    if (landsOnFront && advance && this.uniq.length > 1) {
      // swap BEFORE the flip: the front faces away right now, so the flip
      // itself reveals the new card — and the dot travels WITH the reveal
      this.cardIdx = (this.cardIdx + (dir > 0 ? 1 : this.uniq.length - 1)) % this.uniq.length;
      this.setNcArt();
    }
    this.half += dir;
    this.applyNc(true);
  }
  applyNc(animate, fast = false) {
    // when the closed card faces away, the flap (front cover + blank inside)
    // must hide at edge-on so the REAL back (About-the-art) shows — not the
    // blank inside riding on top of it
    // flap swaps visibility at EDGE-ON in BOTH directions: going to the back
    // it vanishes as the card turns away; coming back it reappears only as the
    // card turns toward you — the About-panel is never covered early
    const backView = !this.open && ((this.half % 2) + 2) % 2 === 1;
    clearTimeout(this._flapVisT);
    const atEdge = !animate ? 0 : fast ? 170 : 380;   // edge-on scales with flight speed (.38s vs 1.05s)
    // atEdge 0 must apply SYNCHRONOUSLY — a setTimeout(0) lands one frame after the
    // geometry snap, and that one flap-less frame is a visible blink (face + shadow)
    const setVis = (v) => { this.flap.style.visibility = v; };
    if (backView) { if (atEdge) this._flapVisT = setTimeout(() => setVis("hidden"), atEdge); else setVis("hidden"); }
    else if (this.flap.style.visibility === "hidden") {
      if (atEdge) this._flapVisT = setTimeout(() => setVis("visible"), atEdge); else setVis("visible");
    } else setVis("visible");
    const ncLand = this.orient?.[this.cardIdx] === "l";
    // The open-lean tilts the card a few degrees so the opening edge reads as
    // depth rather than a flat cut-out. On a side-fold the flap swings LEFT, so
    // the lean belongs on Y. On a top-fold it lifts UPWARD, and a Y lean makes
    // the card sway sideways while the flap goes up — the two motions fight,
    // which is what reads as unnatural. Lean on the same axis as the fold.
    const spinDeg = this.half * -180 + (this.open && !ncLand ? -14 : 0);
    const openTilt = this.open && ncLand ? " rotateX(-12deg)" : "";
    const t = !animate ? "none" : fast ? "transform .38s cubic-bezier(.35,.15,.3,1)" : "";
    [this.spin, this.center].forEach((el) => { el.style.transition = t; });
    this.spin.style.transform = `rotateY(${spinDeg}deg)${openTilt}`;
    this.flap.style.transition = t;
    // Side-fold swings the flap out to the LEFT about its left edge; a landscape
    // top-fold lifts it UP and over about its top edge. Same angle, different axis.
    this.flap.style.transform = ncLand
      ? `rotateX(${this.open ? 164 : 0}deg)`
      : `rotateY(${this.open ? -164 : 0}deg)`;
    // Opening doubles the spread — a panel-width to the LEFT on a side-fold, a
    // panel-height UPWARD on a top-fold. Centered placements (popup / inline)
    // re-center it with the opposite nudge. The dock is pinned to the screen's
    // right edge, so there a nudge would push the base off-screen — the mini
    // instead opens into view with the base staying put.
    this.center.style.transform =
      this.open && !this.hasAttribute("mini")
        ? (ncLand ? "translateY(46%)" : "translateX(46%)")
        : "translate(0)";
  }

  /* ================= auto-play: run → rest 6s after touch → resume ========= */
  autoPlay() {
    // Reduced motion (WCAG 2.2.2 / 2.3.3, 2026-10-07): nothing advances on its
    // own. The shopper can still step through by hand, or press Play.
    if (!this._motionOk && matchMedia("(prefers-reduced-motion: reduce)").matches) { this.timer = null; return; }
    const IDLE = +this.getAttribute("resume-delay") || 2500;   // snappier re-engage after interaction
    const step = () => {
      // a fresh pick holds via lastTouch (pick-hold), then the loop RESUMES and
      // tours the whole picked deck — no permanent freeze while building
      const busy = this._present || this._hoverHold || this._focusHold
        || (this.lastTouch && Date.now() - this.lastTouch < IDLE);
      if (!busy) {
        if (this.hasAttribute("fronts-only")) {
          // ONLY an explicit `fronts-only` (the passive banner) skips backs; the dock
          // + popup do the FULL front/back show. Calendar/notecard tour picked only.
          if (this.product === "calendar") this.building ? this.calGoPicked() : this.calNext();
          else if (this.product === "notecard") this.ncJump((this.cardIdx + 1) % this.uniq.length, 1);
          else this.go((this.cardIdx + 1) % this.uniq.length, "front", 1);
        }
        else if (this.product === "calendar") this.building ? this.calGoPicked() : this.calNext();
        else if (this.product === "notecard") {
          // the show: front → peek open → close → flip to back → flip to next card
          const ph = this.ncPhase % 4;
          if (ph === 0) this.setNc(true, true);        // peekaboo open
          else if (ph === 1) this.setNc(false, true);  // close
          else this.ncFlip(1, ph === 3);               // to back, then to next front
          this.ncPhase += 1;
        } else {                               // engine show: back, then next front
          if (this.showing === "front") this.go(this.cardIdx, "back", 1);
          else this.go((this.cardIdx + 1) % this.uniq.length, "front", 1);
        }
      }
      const iv = +this.getAttribute("interval") || 0;
      const delay = iv || (this.product === "calendar" ? 2600
        : this.product === "notecard" ? [1600, 1500, 1900, 2100][this.ncPhase % 4]
        : 3200);
      this.timer = setTimeout(step, delay);
    };
    this.timer = setTimeout(step, 700);   // start the show promptly
  }
}
customElements.define("adg-product-preview", ADGProductPreview);
